import type { Severity } from '@models/severity';

export type BaseColor =
  | 'dark'
  | 'gray'
  | 'red'
  | 'pink'
  | 'grape'
  | 'violet'
  | 'indigo'
  | 'blue'
  | 'cyan'
  | 'green'
  | 'lime'
  | 'yellow'
  | 'orange'
  | 'teal';

export type SpecialColor = (typeof specialColors)[number];

export type CustomColor =
  | 'mouse-over'
  | `status-${Severity}`
  | `status-${Severity}-light`
  | `status-${Severity}-dark`
  | 'paper';

export type IntrinsicColor = 'inherit' | 'initial' | 'unset' | 'currentcolor' | 'transparent';

export type Color = BaseColor | SpecialColor | CustomColor | IntrinsicColor;

export type ExtendedColor =
  | 'black'
  | 'white'
  | Color
  | `${BaseColor | SpecialColor | CustomColor}.${Shade}`
  | `${CustomColor}-${'light' | 'dark'}`;

type Variant = 'filled' | 'filled-hover' | 'light' | 'light-hover' | 'light-color' | 'outline' | 'outline-hover';
type PrimaryVariant = 'contrast' | 'filled' | 'filled-hover' | 'light' | 'light-hover' | 'light-color';

type Shade = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9;

const specialColors = ['black', 'white', 'dimmed'] as const;

export function themeColor<TColor extends 'black' | 'white'>(color: TColor): `var(--mantine-color-${TColor})`;
export function themeColor(color: 'primary'): `var(--mantine-primary-color-filled)`;
export function themeColor<TShade extends Shade | PrimaryVariant>(
  color: 'primary',
  shade: TShade,
): `var(--mantine-primary-color-${TShade})`;
export function themeColor<TColor extends Color>(color: TColor): `var(--mantine-color-${TColor}-filled)`;
export function themeColor<TColor extends Color, TShade extends Shade | Variant>(
  color: TColor,
  shade: TShade,
): `var(--mantine-color-${TColor}-${TShade})`;
export function themeColor(color: string, shade: string | number = 'filled'): string {
  if (specialColors.includes(color as SpecialColor)) {
    return `var(--mantine-color-${color})`;
  }

  if (color === 'primary') {
    return `var(--mantine-primary-color-${shade})`;
  }

  return `var(--mantine-color-${color}-${shade})`;
}

export function stateColor<TSeverity extends Severity>(
  severity: TSeverity,
): `var(--mantine-color-status-${TSeverity}-filled)`;
export function stateColor<TSeverity extends Severity, TShade extends Shade | Variant>(
  severity: TSeverity,
  shade: TShade,
): `var(--mantine-color-status-${TSeverity}-${TShade})`;
export function stateColor(severity: Severity, shade: Shade | Variant = 'filled'): string {
  return themeColor(`status-${severity}`, shade);
}
